import React, { useState } from "react";
import { Phone, Mail, MapPin, Linkedin, MessageCircle, Send, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CONTACT } from "../data/content";
import { submitContact } from "../lib/api";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const c = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await submitContact(form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">Contact</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Let&apos;s talk about your security priorities
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Whether you&apos;re preparing for an audit, evaluating your first CISO-as-a-service engagement, or
              hardening a cloud rollout — our consultants are ready.
            </p>
            <div className="mt-8 space-y-4">
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 card-lift">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Phone className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Call</div>
                  <div className="font-semibold">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 card-lift">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Mail className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
                  <div className="font-semibold">{CONTACT.email}</div>
                </div>
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 card-lift">
                <div className="h-11 w-11 rounded-xl bg-secondary/10 text-secondary grid place-items-center"><MessageCircle className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">WhatsApp</div>
                  <div className="font-semibold">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={CONTACT.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 card-lift">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Linkedin className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">LinkedIn</div>
                  <div className="font-semibold">Follow Techmate</div>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-4">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><MapPin className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Office</div>
                  <div className="font-semibold">{CONTACT.address}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="contact-form">
              <h3 className="font-display text-xl sm:text-2xl font-semibold">Send us a message</h3>
              <p className="text-sm text-muted-foreground mt-1">We respond within one business day.</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ct-name">Full name*</Label>
                  <Input id="ct-name" data-testid="ct-name-input" value={form.name} onChange={c("name")} required />
                </div>
                <div>
                  <Label htmlFor="ct-email">Email*</Label>
                  <Input id="ct-email" data-testid="ct-email-input" type="email" value={form.email} onChange={c("email")} required />
                </div>
                <div>
                  <Label htmlFor="ct-phone">Phone</Label>
                  <Input id="ct-phone" data-testid="ct-phone-input" value={form.phone} onChange={c("phone")} />
                </div>
                <div>
                  <Label htmlFor="ct-company">Company</Label>
                  <Input id="ct-company" data-testid="ct-company-input" value={form.company} onChange={c("company")} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ct-subject">Subject</Label>
                  <Input id="ct-subject" data-testid="ct-subject-input" value={form.subject} onChange={c("subject")} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ct-msg">Message*</Label>
                  <Textarea id="ct-msg" data-testid="ct-message-input" rows={5} value={form.message} onChange={c("message")} required />
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="ct-submit-btn" className="mt-6 bg-primary hover:bg-primary/90 rounded-full h-11 px-6">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="mr-2 h-4 w-4" /> Send Message</>)}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
